import type {
    ChangePasswordParams,
    ForgotPasswordParams,
    LoginCredentials,
    LoginResponseModel,
    LoginResponseRaw,
    MeResponseRaw,
    MeResult,
    ResetPasswordParams,
    ValidateTokenParams
} from '@/domain/models/auth.js';
import { LoginResponseModel as LoginResponseModelClass } from '@/domain/models/auth.js';
import { UserModel as UserModelClass } from '@/domain/models/user.js';
import type { HttpClient } from '@/domain/protocols/http-client.js';
import type { AuthRepository } from '@/domain/repositories/auth-repository.js';

export class AuthRepositoryImpl implements AuthRepository {
    constructor(private readonly httpClient: HttpClient) {}

    public async login(params: LoginCredentials): Promise<LoginResponseModel> {
        const response = await this.httpClient.request<LoginResponseRaw>({ url: '/api/auth/login', method: 'POST', body: params });
        return LoginResponseModelClass.withFromResponse(response.body);
    }

    public async logout(): Promise<void> {
        await this.httpClient.request({ url: '/api/auth/logout', method: 'POST' });
    }

    public async getMe(): Promise<MeResult> {
        const response = await this.httpClient.request<MeResponseRaw>({ url: '/api/auth/me', method: 'GET' });
        const { theme, ...user } = response.body;
        return {
            user: UserModelClass.withFromResponse(user),
            theme: theme ?? null
        };
    }

    public async forgotPassword(params: ForgotPasswordParams): Promise<void> {
        await this.httpClient.request({ url: '/api/auth/forgot-password', method: 'POST', body: params });
    }

    public async resetPassword(params: ResetPasswordParams): Promise<void> {
        await this.httpClient.request({ url: '/api/auth/reset-password', method: 'POST', body: params });
    }

    public async validateResetToken(params: ValidateTokenParams): Promise<boolean> {
        const response = await this.httpClient.request<{ valid: boolean }>({
            url: `/api/auth/validate-reset-token?token=${encodeURIComponent(params.token)}`,
            method: 'GET'
        });
        return response.body.valid;
    }

    public async firstAccess(params: ResetPasswordParams): Promise<void> {
        await this.httpClient.request({ url: '/api/auth/first-access', method: 'POST', body: params });
    }

    public async changePassword(params: ChangePasswordParams): Promise<void> {
        await this.httpClient.request({ url: '/api/auth/me/password', method: 'PUT', body: params });
    }

    public async getEntraOptions(): Promise<{ enabled: boolean }> {
        const response = await this.httpClient.request<{ enabled: boolean }>({ url: '/api/auth/entra/options', method: 'GET' });
        return { enabled: response.body.enabled === true };
    }

    public async redeemEntraTicket(ticket: string): Promise<LoginResponseModel> {
        const response = await this.httpClient.request<LoginResponseRaw>({ url: '/api/auth/entra/redeem', method: 'POST', body: { ticket } });
        return LoginResponseModelClass.withFromResponse(response.body);
    }
}
